import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import SchoolListingCard from "@/components/SchoolListingCard";
import usePageMeta from "@/hooks/usePageMeta";
import useBranding, { fillTemplate } from "@/hooks/useBranding";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, ShieldCheck, GitCompareArrows, Search, LocateFixed } from "lucide-react";
import { toast } from "sonner";

export default function Schools() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { param } = useParams();
  const citySlug = param && param.startsWith("in-") ? param.slice(3) : "";
  const initCity = citySlug ? citySlug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : (params.get("city") || "");
  const [items, setItems] = useState([]);
  const brand = useBranding();
  const [cities, setCities] = useState([]);
  const [cityContent, setCityContent] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(initCity);
  const [board, setBoard] = useState(params.get("board") || "");
  const [type, setType] = useState(params.get("school_type") || "");
  const [sort, setSort] = useState("");
  const [compare, setCompare] = useState([]);
  const [page, setPage] = useState(1);

  // Reset pagination whenever the filtered result set changes
  useEffect(() => { setPage(1); }, [items.length, q, city, board, type, sort]);

  const brandSuffix = brand?.site_title ? ` — ${brand.site_title}` : "";
  const year = brand?.default_year || "2026-27";
  const tplTitle = brand?.seo_title_template ? fillTemplate(brand.seo_title_template, city, year) : "";
  const tplMeta = brand?.seo_meta_template ? fillTemplate(brand.seo_meta_template, city, year) : "";
  const tplDesc = brand?.city_desc_template ? fillTemplate(brand.city_desc_template, city, year) : "";
  const pageTitle = city
    ? (cityContent?.seo_title || (tplTitle ? `${tplTitle}${brandSuffix}` : `Best Schools in ${city}${brandSuffix}`))
    : `Browse Schools${brandSuffix}`;
  const pageDesc = city
    ? (cityContent?.meta_description || tplMeta || `Discover top schools in ${city}. Compare fees, boards, facilities and enquire directly.`)
    : (brand?.site_description || "Discover, compare and enquire with top schools across India.");
  usePageMeta(pageTitle, pageDesc);

  const load = async () => {
    const p = {};
    if (q) p.q = q;
    if (city) p.city = city;
    if (board) p.board = board;
    if (type) p.school_type = type;
    if (sort) p.sort = sort;
    const { data } = await api.get("/schools", { params: p });
    setItems(data);
  };

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);

  // Sync city state when the URL param changes (e.g., footer city link clicks).
  // Without this, initCity is captured only at first mount and further /schools/in-<city>
  // navigations only change the URL, not the visible content.
  useEffect(() => {
    setCity(initCity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  useEffect(() => {
    if (city) { api.get(`/city-content/${encodeURIComponent(city)}`).then(r => setCityContent(r.data)).catch(() => setCityContent(null));
      // send city param exactly as user typed; backend regex is case-insensitive
    }
    else setCityContent(null);
  }, [city]);
  useEffect(() => {
    const slug = city ? `/schools/in-${city.toLowerCase().replace(/\s+/g, "-")}` : "/schools";
    if (window.location.pathname !== slug) nav(slug, { replace: true });
    load(); /* eslint-disable-next-line */
  }, [city, sort]);

  const detectLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    toast.loading("Detecting location…", { id: "geo" });
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
        const data = await r.json();
        const detected = data.address?.city || data.address?.town || data.address?.state_district || data.address?.state || "";
        if (detected) { setCity(detected); toast.success(`Location: ${detected}`, { id: "geo" }); setTimeout(load, 100); }
        else toast.error("Could not detect city", { id: "geo" });
      } catch { toast.error("Location lookup failed", { id: "geo" }); }
    }, () => toast.error("Permission denied", { id: "geo" }));
  };

  const toggleCompare = (id) => {
    setCompare((c) => {
      if (c.includes(id)) return c.filter((x) => x !== id);
      if (c.length >= 3) { toast.error("Compare up to 3 schools"); return c; }
      return [...c, id];
    });
  };

  const compareItems = items.filter((s) => compare.includes(s.id));

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <PublicNav />

      <div className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="schools-heading">
          {city ? (tplTitle || `Schools in ${city}`) : "Schools"}
        </h1>
        <p className="text-slate-500 mt-2">Browse and filter schools across India</p>
        {city && (cityContent?.description || tplDesc) && (
          <div
            className="mt-5 relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            data-testid="city-description"
          >
            <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-blue-600 to-indigo-600" />
            <div className="pl-6 pr-5 py-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] uppercase tracking-widest font-bold text-blue-700">About {city}</span>
                {!cityContent?.description && (
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Auto-generated</span>
                )}
              </div>
              <p className="text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">
                {cityContent?.description || tplDesc}
              </p>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-6 gap-3 bg-white p-4 rounded-lg border border-slate-200">
          <div className="md:col-span-2 flex items-center gap-2 border border-slate-200 rounded px-3">
            <Search className="w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name, city..." value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()} className="border-0 shadow-none focus-visible:ring-0"
                   data-testid="filter-search-input" />
          </div>
          <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} data-testid="filter-city-input" />
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="filter-city-select"><SelectValue placeholder="Or pick city" /></SelectTrigger>
            <SelectContent>{cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={board} onValueChange={setBoard}>
            <SelectTrigger data-testid="filter-board-select"><SelectValue placeholder="Board" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="CBSE">CBSE</SelectItem>
              <SelectItem value="ICSE">ICSE</SelectItem>
              <SelectItem value="State">State</SelectItem>
              <SelectItem value="IB">IB</SelectItem>
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="filter-type-select"><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Pre">Pre-Primary</SelectItem>
              <SelectItem value="Primary">Primary</SelectItem>
              <SelectItem value="Middle">Middle</SelectItem>
              <SelectItem value="Secondary">Secondary</SelectItem>
              <SelectItem value="Sr. Secondary">Sr. Secondary</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="filter-sort-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name (A-Z)</SelectItem>
              <SelectItem value="fees_asc">Fees (Low → High)</SelectItem>
              <SelectItem value="fees_desc">Fees (High → Low)</SelectItem>
              <SelectItem value="size_desc">Size (Acres, desc)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-between items-center mt-3 gap-3 flex-wrap">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="apply-filters-btn">Apply Filters</Button>
            <Button variant="outline" size="sm" onClick={detectLocation} data-testid="detect-location-btn"><LocateFixed className="w-4 h-4 mr-1" />Near Me</Button>
          </div>
          <div className="text-sm text-slate-500" data-testid="schools-count">{items.length} school{items.length !== 1 && "s"} found</div>
        </div>

        {/* Compare bar */}
        {compare.length > 0 && (
          <div className="mt-4 bg-slate-900 text-white rounded-lg p-4 flex items-center justify-between" data-testid="compare-bar">
            <div className="flex items-center gap-3 text-sm">
              <GitCompareArrows className="w-4 h-4 text-amber-400" />
              Comparing {compare.length} school{compare.length > 1 && "s"}
            </div>
            <Button size="sm" variant="secondary" onClick={() => setCompare([])} data-testid="clear-compare-btn">Clear</Button>
          </div>
        )}

        {/* Sticky "Compare Now" pill — shows only when 2+ schools are selected */}
        {compare.length >= 2 && (
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="compare-table"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="fixed right-4 top-1/2 -translate-y-1/2 z-40 bg-amber-500 hover:bg-amber-600 active:scale-95 text-white text-xs font-bold px-3 py-2 rounded-full shadow-lg flex items-center gap-1.5 transition"
            data-testid="compare-now-btn"
          >
            <GitCompareArrows className="w-3.5 h-3.5" />
            Compare Now ({compare.length})
          </button>
        )}

        {/* Cards + pagination (10 per page, URL/title unchanged) */}
        <div className="mt-6 flex flex-col gap-5">
          {(() => {
            const PER_PAGE = 10;
            const total = items.length;
            const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
            const p = Math.min(page, totalPages);
            const slice = items.slice((p - 1) * PER_PAGE, p * PER_PAGE);
            return (
              <>
                {slice.map((s) => (
                  <SchoolListingCard
                    key={s.id}
                    school={s}
                    compare={compare}
                    toggleCompare={toggleCompare}
                    onEnquire={(sc) => nav(`/schools/${sc.slug || sc.id}?enquire=1`)}
                  />
                ))}
                {items.length === 0 && (
                  <div className="text-center py-16 text-slate-500 bg-white rounded-2xl border border-slate-200" data-testid="no-schools-msg">
                    No schools match the current filters. Try clearing filters or exploring another city.
                  </div>
                )}
                {total > PER_PAGE && (
                  <div className="flex items-center justify-between mt-2 gap-3" data-testid="schools-pagination">
                    <Button variant="outline" size="sm" disabled={p <= 1} onClick={() => setPage(p - 1)} data-testid="schools-prev-btn">‹ Prev</Button>
                    <div className="text-xs text-slate-500">Page <b className="text-slate-800">{p}</b> of {totalPages} · {total} schools</div>
                    <Button variant="outline" size="sm" disabled={p >= totalPages} onClick={() => setPage(p + 1)} data-testid="schools-next-btn">Next ›</Button>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Compare table */}
        {compareItems.length >= 2 && (
          <div className="mt-12 bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-4 text-left font-semibold text-slate-500 uppercase text-xs tracking-wider">Feature</th>
                  {compareItems.map((s) => (
                    <th key={s.id} className="p-4 text-left font-bold">{s.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["City", (s) => s.city],
                  ["Board", (s) => s.board],
                  ["Type", (s) => s.school_type],
                  ["Established", (s) => s.establishment_year],
                  ["Size (acres)", (s) => s.size_acres],
                  ["Fees range", (s) => `₹${(s.fees_min || 0).toLocaleString()} - ₹${(s.fees_max || 0).toLocaleString()}`],
                  ["Facilities", (s) => (s.facilities || []).join(", ")],
                  ["Admission", (s) => s.admission_open ? "Open" : "Closed"],
                ].map(([label, fn]) => (
                  <tr key={label} className="border-t border-slate-100">
                    <td className="p-4 text-slate-500 font-medium">{label}</td>
                    {compareItems.map((s) => <td key={s.id} className="p-4 text-slate-900">{fn(s)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}
